import React from 'react';
import { Button } from '@components/common/button';
import { LoadingSpinner } from '@components/common/loading_spinner';
import {
  ArrowLeft,
  Download,
  Send,
  Printer,
  Edit,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Invoice, InvoiceTemplate as InvoiceTemplateType } from '@/types/invoice_types';

interface InvoicePreviewProps {
  invoice: Invoice | null;
  template_config: InvoiceTemplateType;
  on_download?: (invoice: Invoice) => void;
  on_send?: (invoice: Invoice) => void;
  on_edit?: (invoice: Invoice) => void;
  on_print?: () => void;
  on_back?: () => void;
  is_loading?: boolean;
}

export const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoice,
  template_config,
  on_download,
  on_send,
  on_edit,
  on_print,
  on_back,
  is_loading = false,
}) => {
  if (is_loading) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading invoice..." />
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="min-h-screen bg-off-white flex items-center justify-center">
        <div className="text-center">
          <FileText size={48} className="mx-auto text-medium-gray mb-4" />
          <p className="text-medium-gray">Invoice not found</p>
          <Button variant="primary" onClick={on_back} className="mt-4">Back to Invoices</Button>
        </div>
      </div>
    );
  }

  const tc  = template_config as any;
  const inv = invoice as any;

  // ── Formatters ──────────────────────────────────────────────────────────────
  const fmt_kes = (amount: string | number | null | undefined): string => {
    try {
      const n = parseFloat(String(amount ?? 0));
      if (isNaN(n)) return 'Kshs. 0/=';
      return n % 1 === 0
        ? `Kshs. ${n.toLocaleString('en-KE', { maximumFractionDigits: 0 })}/=`
        : `Kshs. ${n.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch { return 'Kshs. 0/='; }
  };

  const fmt_date = (d: string | null | undefined): string => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const fmt_reading = (v: string | number | null | undefined): string =>
    v != null ? parseFloat(String(v)).toFixed(0) : '—';

  // ── Data mapping ─────────────────────────────────────────────────────────────
  // Readings: prefer top-level enriched fields injected by _enrich_invoice(),
  // fall back to first item fields for the detail endpoint.
  const primary_item = inv.items?.[0] as any;

  const prev_reading: string | null =
    inv.previous_reading         ??
    primary_item?.previous_reading ??
    null;

  const curr_reading: string | null =
    inv.current_reading          ??
    primary_item?.current_reading  ??
    null;

  const has_readings = prev_reading != null && curr_reading != null;

  // units_consumed: top-level enriched value > derive from readings > item qty
  const units_consumed: string =
    inv.units_consumed != null
      ? parseFloat(inv.units_consumed).toFixed(0)
      : has_readings
        ? (parseFloat(curr_reading!) - parseFloat(prev_reading!)).toFixed(0)
        : primary_item?.quantity
          ? parseFloat(primary_item.quantity).toFixed(0)
          : '—';

  // Balance fields — all provided by the enriched viewset response
  const tax_val           = parseFloat(inv.tax_amount   || 0);
  const subtotal_val      = parseFloat(inv.subtotal     || 0);
  const total_val         = parseFloat(inv.total_amount || 0);
  const display_total     = tax_val > 0 ? subtotal_val : total_val;
  const prev_balance      = parseFloat(inv.previous_balance  || 0);
  const payments_received = parseFloat(inv.payments_received || 0);
  // Use pre-computed backend value when available
  const balance_due =
    inv.balance_carried_forward != null
      ? parseFloat(inv.balance_carried_forward)
      : display_total + prev_balance - payments_received;

  // Client detail fields (present on detail endpoint; may be absent on list)
  const client_name    = inv.client_name    || '—';
  const client_email   = inv.client_email   || '';
  const client_address = inv.client_address || '';
  const client_phone   = inv.client_phone   || '';
  const client_postal  = inv.client_postal  || '';
  const client_type    = inv.client_type    || '';

  // ── Status banner ────────────────────────────────────────────────────────────
  const status_lower = invoice.status.toLowerCase();
  const status_banners: Record<string, React.ReactNode> = {
    draft: (
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <Edit size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-charcoal">Draft Invoice</p>
          <p className="text-xs text-medium-gray mt-0.5">Send to the client via email or download as PDF.</p>
        </div>
      </div>
    ),
    sent: balance_due > 0 ? (
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-md">
        <Clock size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-charcoal">Pending Payment</p>
          <p className="text-xs text-medium-gray mt-0.5">Balance due: {fmt_kes(balance_due)}</p>
        </div>
      </div>
    ) : null,
    paid: (
      <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-md">
        <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-charcoal">Paid Invoice</p>
          <p className="text-xs text-medium-gray mt-0.5">This invoice has been fully settled.</p>
        </div>
      </div>
    ),
    overdue: (
      <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md">
        <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-charcoal">Overdue — {inv.days_overdue || 0} days</p>
          <p className="text-xs text-medium-gray mt-0.5">Balance due: {fmt_kes(balance_due)}</p>
        </div>
      </div>
    ),
  };

  // ── Company constants ────────────────────────────────────────────────────────
  const CO_NAME   = 'KIMANI WATHIGO ESTATE WATER PROJECT';
  const CO_ADDRESS = tc.company_address || 'P.O.BOX 64-00605, UTHIRU';
  const CO_PHONE  = tc.company_phone   || '';
  const CO_EMAIL  = tc.company_email   || '';
  const BIZ_NUM   = tc.business_number || '329329';
  const ACCT_NUM  = tc.account_number  || '0100346540800';
  const BANK_NAME = tc.bank_name       || 'Standard Chartered Bank, Nairobi Branch';

  // ── Shared cell styles ────────────────────────────────────────────────────────
  const label_cls = 'text-[11px] text-[#888] leading-snug';
  const value_cls = 'text-[12px] text-[#111] leading-snug';
  const bold_cls  = 'text-[12px] font-bold text-[#111] leading-snug';
  const sec_cls   = 'text-[10px] font-bold text-[#888] uppercase tracking-widest';

  return (
    <div className="min-h-screen bg-[#f0f0ee]">

      {/* ── Sticky action bar ─────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-[#ddd] sticky top-0 z-10 print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="secondary" size="sm" onClick={on_back} icon_left={<ArrowLeft size={16} />}>
              Back
            </Button>
            <div>
              <p className="text-base font-bold text-charcoal leading-none">Invoice Preview</p>
              <p className="text-xs text-medium-gray mt-0.5">{invoice.invoice_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {on_edit && status_lower === 'draft' && (
              <Button variant="secondary" size="sm" onClick={() => on_edit(invoice)} icon_left={<Edit size={15} />}>Edit</Button>
            )}
            <Button variant="secondary" size="sm" onClick={on_print} icon_left={<Printer size={15} />}>Print</Button>
            {on_download && (
              <Button variant="secondary" size="sm" onClick={() => on_download(invoice)} icon_left={<Download size={15} />}>PDF</Button>
            )}
            {on_send && status_lower === 'draft' && (
              <Button variant="primary" size="sm" onClick={() => on_send(invoice)} icon_left={<Send size={15} />}>Send</Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">

        {/* Status banner */}
        {status_banners[status_lower] && (
          <div className="mb-5 print:hidden">{status_banners[status_lower]}</div>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            INVOICE DOCUMENT
        ════════════════════════════════════════════════════════════════════ */}
        <div
          className="bg-white shadow-sm"
          style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif", padding: '40px 44px 36px' }}
        >

          {/* ── SECTION 1: INVOICE title + Company ─────────────────────────── */}
          <div className="flex items-end justify-between">
            <span style={{ fontSize: 46, fontWeight: 900, letterSpacing: '-1px', color: '#111', lineHeight: 1 }}>
              INVOICE
            </span>
            <div className="text-right">
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', lineHeight: 1.3 }}>{CO_NAME}</p>
              <p style={{ fontSize: 11, color: '#666', lineHeight: 1.4 }}>{CO_ADDRESS}</p>
              {CO_PHONE && <p style={{ fontSize: 11, color: '#666' }}>{CO_PHONE}</p>}
              {CO_EMAIL && <p style={{ fontSize: 11, color: '#666' }}>{CO_EMAIL}</p>}
            </div>
          </div>

          {/* Thick black rule */}
          <div style={{ borderTop: '2px solid #111', marginTop: 8, marginBottom: 20 }} />

          {/* ── SECTION 2: Bill To + Invoice Meta ──────────────────────────── */}
          <div className="flex justify-between gap-8 mb-8">
            {/* Bill To */}
            <div style={{ minWidth: 180 }}>
              <p style={{ fontSize: 10, color: '#888', fontStyle: 'italic', marginBottom: 6 }}>Bill to :</p>
              <p style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 2 }}>{client_name}</p>
              {client_email   && <p style={{ fontSize: 11, color: '#333' }}>{client_email}</p>}
              {client_address && <p style={{ fontSize: 11, color: '#333' }}>{client_address}</p>}
              {client_phone   && <p style={{ fontSize: 11, color: '#333' }}>{client_phone}</p>}
              {client_type    && (
                <p style={{ fontSize: 10, color: '#888', marginTop: 2, textTransform: 'capitalize' }}>
                  {client_type.toLowerCase()}
                </p>
              )}
            </div>

            {/* Invoice meta */}
            <div style={{ minWidth: 220 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {([
                    ['Invoice No.',   invoice.invoice_number],
                    ['Billing Date',  fmt_date(inv.issue_date)],
                    ['Due Date',      fmt_date(invoice.due_date)],
                    ...(invoice.billing_period_start ? [[
                      'Billing Period',
                      `${fmt_date(invoice.billing_period_start)} – ${fmt_date(invoice.billing_period_end)}`
                    ]] : []),
                  ] as [string, string][]).map(([label, val]) => (
                    <tr key={label}>
                      <td style={{ fontSize: 11, color: '#888', paddingBottom: 5, paddingRight: 12, whiteSpace: 'nowrap' }}>{label}</td>
                      <td style={{ fontSize: 11, fontWeight: 700, color: '#111', textAlign: 'right', paddingBottom: 5 }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── SECTION 3: Consumption + Customer Details ──────────────────── */}
          <div className="flex justify-between gap-8 mb-6">
            {/* Consumption Details */}
            <div style={{ minWidth: 160 }}>
              <p className={sec_cls} style={{ marginBottom: 8 }}>Consumption Details</p>
              {has_readings && (
                <>
                  <div className="flex justify-between gap-6 mb-1">
                    <span className={label_cls}>Last meter reading:</span>
                    <span className={value_cls}>{fmt_reading(prev_reading)}</span>
                  </div>
                  <div className="flex justify-between gap-6 mb-1">
                    <span className={label_cls}>Current meter reading:</span>
                    <span className={value_cls}>{fmt_reading(curr_reading)}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between gap-6">
                <span className={label_cls}>Units consumed:</span>
                <span className={bold_cls}>{units_consumed}</span>
              </div>
            </div>

            {/* Customer Details */}
            <div style={{ minWidth: 260 }}>
              <p className={sec_cls} style={{ marginBottom: 8 }}>Customer Details</p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {([
                    ['Client Name:',    client_name.toUpperCase()],
                    ['Location:',       client_address],
                    ['Postal address:', client_postal],
                    ['Telephone:',      client_phone],
                  ] as [string, string][]).map(([lbl, val]) => (
                    <tr key={lbl}>
                      <td style={{ fontSize: 11, color: '#888', paddingBottom: 4, paddingRight: 10, whiteSpace: 'nowrap' }}>{lbl}</td>
                      <td style={{ fontSize: 11, fontWeight: lbl === 'Client Name:' ? 700 : 400, color: '#111', paddingBottom: 4 }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Thin rule */}
          <div style={{ borderTop: '1px solid #ccc', marginBottom: 16 }} />

          {/* ── SECTION 4: Line-items table ────────────────────────────────── */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
            <thead>
              <tr style={{ background: '#eee' }}>
                {['Item Description', 'Units', 'Unit Price', 'Subtotal'].map((h, i) => (
                  <th
                    key={h}
                    style={{
                      fontSize: 10, fontWeight: 700, color: '#888',
                      textAlign: i === 0 ? 'left' : 'right',
                      padding: '7px 6px',
                      borderBottom: '1px solid #ccc',
                      borderTop: '1px solid #ccc',
                      letterSpacing: '0.03em',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {inv.items?.map((item: any, idx: number) => {
                // Per-item readings first, then fall back to top-level enriched values
                const pr  = item.previous_reading ?? prev_reading;
                const cr  = item.current_reading  ?? curr_reading;
                const qty = pr != null && cr != null
                  ? parseFloat(cr) - parseFloat(pr)
                  : parseFloat(item.quantity || 0);
                const is_last = idx === (inv.items?.length ?? 0) - 1;
                return (
                  <tr key={item.id || idx}>
                    <td style={{ padding: '9px 6px 9px 0', borderBottom: `1px solid ${is_last ? '#aaa' : '#e0e0e0'}`, verticalAlign: 'top' }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: '#111', margin: 0 }}>{item.description}</p>
                      {pr != null && cr != null && (
                        <p style={{ fontSize: 10, color: '#888', margin: '2px 0 0', fontStyle: 'italic' }}>
                          Previous: {parseFloat(pr).toFixed(2)}&nbsp;·&nbsp;Current: {parseFloat(cr).toFixed(2)}
                        </p>
                      )}
                    </td>
                    <td style={{ padding: '9px 6px', textAlign: 'right', fontSize: 12, color: '#333', borderBottom: `1px solid ${is_last ? '#aaa' : '#e0e0e0'}`, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {qty.toFixed(2)}
                    </td>
                    <td style={{ padding: '9px 6px', textAlign: 'right', fontSize: 12, color: '#333', borderBottom: `1px solid ${is_last ? '#aaa' : '#e0e0e0'}`, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {fmt_kes(item.unit_price)}
                    </td>
                    <td style={{ padding: '9px 6px 9px 0', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#111', borderBottom: `1px solid ${is_last ? '#aaa' : '#e0e0e0'}`, verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      {fmt_kes(item.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* ── SECTION 5: Totals block ─────────────────────────────────────── */}
          <div className="flex justify-end" style={{ marginBottom: 28 }}>
            <table style={{ minWidth: 280, borderCollapse: 'collapse' }}>
              <tbody>
                <tr>
                  <td style={{ fontSize: 13, fontWeight: 700, color: '#111', paddingTop: 10, paddingBottom: 7, paddingRight: 16, borderTop: '2px solid #111', textAlign: 'right' }}>
                    Current Charges
                  </td>
                  <td style={{ fontSize: 13, fontWeight: 700, color: '#111', paddingTop: 10, paddingBottom: 7, borderTop: '2px solid #111', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmt_kes(display_total)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontSize: 11, color: '#888', paddingBottom: 5, paddingRight: 16, textAlign: 'right' }}>
                    Previous Balance
                  </td>
                  <td style={{ fontSize: 11, color: '#333', paddingBottom: 5, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmt_kes(prev_balance)}
                  </td>
                </tr>
                <tr>
                  <td style={{ fontSize: 11, color: '#888', paddingBottom: 5, paddingRight: 16, textAlign: 'right' }}>
                    Payments Received
                  </td>
                  <td style={{ fontSize: 11, color: '#333', paddingBottom: 5, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    ({fmt_kes(payments_received)})
                  </td>
                </tr>
                <tr>
                  <td style={{ fontSize: 13, fontWeight: 700, color: '#111', paddingTop: 10, paddingBottom: 6, paddingRight: 16, borderTop: '2px solid #111', textAlign: 'right' }}>
                    Balance Due
                  </td>
                  <td style={{
                    fontSize: 13, fontWeight: 700,
                    color: balance_due > 0 ? '#CC0000' : '#111',
                    paddingTop: 10, paddingBottom: 6,
                    borderTop: '2px solid #111',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                  }}>
                    {fmt_kes(balance_due)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Heavy black rule */}
          <div style={{ borderTop: '2px solid #111', marginBottom: 20 }} />

          {/* ── SECTION 6: Payment Details + Company Stamp ─────────────────── */}
          <div className="flex justify-between gap-8" style={{ marginBottom: 28 }}>
            <div style={{ flex: '0 0 60%', maxWidth: '60%' }}>
              <p style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>Payment Details</p>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', marginBottom: 3 }}>Business Number: {BIZ_NUM}</p>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', marginBottom: 3 }}>Deposit payment in Acc. No. {ACCT_NUM}</p>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', marginBottom: 12 }}>Bank: {BANK_NAME}</p>
              <p style={{ fontSize: 10.5, color: '#333', lineHeight: 1.65 }}>
                Dear Customer, please pay the amount due by{' '}
                <strong>{fmt_date(invoice.due_date)}</strong> to avoid disconnection.
                Please note that any outstanding amounts beyond this date will continue to incur
                an accumulating interest charge of <strong>10.00%</strong> per day. If your water
                supply is disconnected, you will be required to pay a reconnection fee of{' '}
                <strong>Kshs.800/=</strong> before your supply is restored. Kindly comply.
              </p>
              {inv.payment_terms && (
                <p style={{ fontSize: 10.5, color: '#888', marginTop: 10, lineHeight: 1.55 }}>
                  {inv.payment_terms}
                </p>
              )}
            </div>
            <div className="text-right" style={{ flex: '0 0 38%', maxWidth: '38%' }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: '#111', lineHeight: 1.4 }}>{CO_NAME}</p>
              <p style={{ fontSize: 11, color: '#888', lineHeight: 1.4 }}>{CO_ADDRESS}</p>
              {CO_EMAIL && <p style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{CO_EMAIL}</p>}
              {CO_PHONE && <p style={{ fontSize: 11, color: '#888' }}>{CO_PHONE}</p>}
            </div>
          </div>

          {/* ── SECTION 7: Footer ──────────────────────────────────────────── */}
          <div style={{ borderTop: '1px solid #ccc', paddingTop: 8, textAlign: 'center' }}>
            <p style={{ fontSize: 9, color: '#888' }}>
              {invoice.invoice_number}&nbsp;·&nbsp;{CO_NAME}&nbsp;·&nbsp;{fmt_date(inv.issue_date)}
            </p>
          </div>

        </div>
      </div>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
    </div>
  );
};